/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    userId: undefined,
    loading: false,
    error: '',
    application: {},
    user: {},
    tenant: {},
    userDepartments: [],
    tenantList: [],
    navigateToLogin: false,
    currentTenantStatus: '',
    organizationSettings: {
      menuFont: 'nunito',
      simpleOrderUI: false,
      combineSameOrders: false,
    },
    register: '',
    userLoginAttempts: { totalCount: 0, items: [] },
  },
  reducers: {
    getOrganizationSettings(state) {
      return { ...state };
    },
    setOrganizationSettings(state, action) {
      state.organizationSettings = action.payload;
    },
    getUserLoginAttempts(state) {
      state.loading = true;
    },
    setUserLoginAttempts(state, action) {
      state.userLoginAttempts = action.payload;
      state.loading = false;
    },
    setCurrentTenantStatus(state, action) {
      state.loading = false;
      state.currentTenantStatus = action.payload;
    },
    getCurrentTenantStatus(state) {
      state.loading = true;
    },
    setCurrentTenant(state) {
      return { ...state };
    },
    getTenantsList(state) {
      state.loading = true;
    },
    setTenantsList(state, action) {
      state.loading = false;
      state.tenantList = action.payload;
    },
    loginUser(state) {
      state.loading = true;
    },
    setUser(state, action) {
      state.userId = action.payload;
      state.loading = false;
    },
    logoutUser(state) {
      state.loading = true;
    },
    loggedOut(state) {
      state.loading = false;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    getCurrentLoginInformation(state) {
      state.loading = true;
    },
    setCurrentLoginInformation: (state, action) => {
      state.loading = false;
      const { application, user, tenant, userDepartments } = action.payload;
      state.application = application;
      state.user = user;
      state.tenant = tenant;
      state.userDepartments = userDepartments;
    },
    changeProfileImage(state) {
      return { ...state };
    },
    setProfilePicture(state, action) {
      const user = { ...state.user };
      user.profilePictureId = action.payload;
      state.user = user;
    },
    sendPasswordResetCode(state) {
      state.loading = true;
    },
    passwordResetCodeSent(state) {
      state.loading = false;
    },
    sendNewPassword(state) {
      state.loading = true;
    },
    newPasswordSent(state) {
      state.loading = false;
      state.navigateToLogin = true;
    },
    resetNavigateToLogin(state) {
      state.navigateToLogin = false;
    },
    changePassword(state) {
      state.loading = true;
    },
    passwordChanged(state) {
      state.loading = false;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    getTenantDisplayInfo(state) {
      return { ...state };
    },
    setTenantDisplayInfo(state, action) {
      state.tenantDisplayInfo = action.payload;
    },
    getRegisterUser(state) {
      return { ...state };
    },
    setRegisterUser(state, action) {
      state.register = action.payload;
    },
    clearRegisterState(state) {
      state.register = null; 
    }
  },
});

export const {
  setIsLoading,
  getOrganizationSettings,
  setOrganizationSettings,
  getUserLoginAttempts,
  setUserLoginAttempts,
  getCurrentTenantStatus,
  setCurrentTenantStatus,
  changePassword,
  passwordChanged,
  sendPasswordResetCode,
  passwordResetCodeSent,
  sendNewPassword,
  newPasswordSent,
  resetNavigateToLogin,
  setCurrentTenant,
  getTenantsList,
  setTenantsList,
  changeProfileImage,
  setProfilePicture,
  loginUser,
  setUser,
  logoutUser,
  loggedOut,
  setError,
  getCurrentLoginInformation,
  setCurrentLoginInformation,
  getTenantDisplayInfo,
  setTenantDisplayInfo,
  getRegisterUser,
  setRegisterUser,
  clearRegisterState
} = authSlice.actions;

export default authSlice.reducer;

export const getAvailableOrganizationSettings = createSelector(
  (state) => state.auth.organizationSettings,
  (organizationSettings) => organizationSettings
);

export const getAvailableUserLoginAttempts = createSelector(
  (state) => state.auth.userLoginAttempts,
  (userLoginAttempts) => userLoginAttempts
);

export const getAvailableCurrentTenantStatus = createSelector(
  (state) => state.auth.currentTenantStatus,
  (currentTenantStatus) => currentTenantStatus
);

export const getAvailableTenantsForSelection = createSelector(
  (state) => state.auth.tenantList,
  (tenantList) => tenantList
);

export const getCurrentUser = createSelector(
  (state) => state.auth.user,
  (user) => user
);

export const getCurrentTenant = createSelector(
  (state) => state.auth.tenant,
  (tenant) => tenant
);

export const isSystemUser = createSelector(
  (state) => state.auth.user,
  (user) => user && user.isSystemUser
);

export const isAdminUser = createSelector(
  (state) => state.auth.user,
  (user) => user && user.isAdmin
);

export const canEditComposition = createSelector(
  (state) => state.auth.user,
  (user) => user && user.canEditComposition
);

export const getCanAssignRoles = createSelector(
  (state) => state.auth.user,
  (user) => user && user.canAssignRoles
);

export const isMasterUser = createSelector(
  (state) => state.auth.user,
  (user) => user && user.isAdmin && user.isSystemUser
);

export const getLoading = createSelector(
  (state) => state.auth.loading,
  (loading) => loading
);

export const getNavigateToLogin = createSelector(
  (state) => state.auth.navigateToLogin,
  (navigateToLogin) => navigateToLogin
);

export const getAppInfo = createSelector(
  (state) => state.auth.application,
  (application) => application
);

export const getUserDepartments = createSelector(
  (state) => state.auth.userDepartments,
  (userDepartments) => userDepartments
);

export const getCurrentTenantDisplayInfo = createSelector(
  (state) => state.auth.tenantDisplayInfo,
  (tenantDisplayInfo) => tenantDisplayInfo
);

export const getUserRegisteration = createSelector(
  (state) => state.auth.register,
  (register) => register
);

